//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box } from '@mui/material'
import { ExchangeRateTypeEnum, PoolTypeEnum, RoleEnum } from '@shared/Enums'
import { EthAddr } from '@shared/SchemaTypes'

import React, { useEffect } from 'react'

import ButtonModal from '@react/components/ButtonModal'
import { useOtChainState, useOtChainTimestamp, useOtSelector } from '@react/redux/store'
import { PoolDynamicOverviewCTO } from '@shared/sharedTypes'

import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import DynamicSetExchangeRateForm from './DynamicSetExchangeRateForm'
import DynamicPauseResumeForm from './DynamicPauseResumeForm'
import AdminApproveTransferDestination from '@react/transfer/AdminApproveTransferDestination'
import { PoolCrossChainTransferCTO, TransferStepNumbers } from '@shared/PoolCrossChainDataCTO'
import { logOT } from '@shared/loggingShared'
import { poolCrossChainTransferFetch } from '@react/fetchers/PoolFetchers'

import AdminPoolRemoveForm from '@react/admin/AdminPoolRemoveForm'
import DynamicSetIndicativeInterestAndCollateralRateForm from './DynamicSetIndicativeAndCollateralRateForm'

export default function DynamicBorrowerAdminPoolActionModals(props: {
  poolAddr: EthAddr
  poolName: string
}): React.ReactElement {
  const { poolAddr } = props

  const {
    stageConfiguration: { stageName, restAPIKey, serverApiBaseUrl },
    userConnection: { roleEnum },
    chainConfiguration: {
      chainId,

      providerReadOnly,
    },
  } = useOtChainState()
  const [poolCrossChainTransferCTOFetched, setPoolCrossChainTransferCTFetched] = React.useState<boolean>(false)
  const [poolCrossChainTransferCTO, setPoolCrossChainTransferCTO] = React.useState<
    PoolCrossChainTransferCTO | undefined
  >(undefined)
  const poolBaseOverviewCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO])

  const poolDynamicOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Dynamic ? (poolBaseOverviewCTO as PoolDynamicOverviewCTO) : undefined

  if (!poolDynamicOverviewCTO || roleEnum === RoleEnum.LENDER) {
    return <div></div>
  }
  const chainTimestamp = useOtChainTimestamp()
  useEffect(() => {
    if (poolAddr && chainId && serverApiBaseUrl && restAPIKey && providerReadOnly) {
      poolCrossChainTransferFetch(serverApiBaseUrl, restAPIKey, poolAddr)
        .then((_poolCrossChainTransferCTO: PoolCrossChainTransferCTO | undefined) => {
          setPoolCrossChainTransferCTFetched(true)
          logOT.info('poolCrossChainTransferCTO fetched ', serverApiBaseUrl, poolAddr, _poolCrossChainTransferCTO)
          setPoolCrossChainTransferCTO(_poolCrossChainTransferCTO)
        })
        .catch(() => {
          console.error('Fetch _poolOffchainDataCTO error')
          setPoolCrossChainTransferCTFetched(true)
          setPoolCrossChainTransferCTO(undefined)
        })
    } else {
      setPoolCrossChainTransferCTO(undefined)
      setPoolCrossChainTransferCTFetched(false)
    }
  }, [chainId, serverApiBaseUrl, providerReadOnly, poolAddr])

  const canApproveTransferDestination =
    roleEnum === RoleEnum.POOL_ADMIN &&
    poolCrossChainTransferCTOFetched &&
    poolCrossChainTransferCTO?.destinationPoolAddr === poolAddr &&
    poolCrossChainTransferCTO?.transferStep === TransferStepNumbers.Source_Approved

  const canRemovePool = roleEnum === RoleEnum.POOL_ADMIN
  const isCompoundingVault = poolDynamicOverviewCTO?.exchangeRateType === ExchangeRateTypeEnum.Compounding
  const chainDayNumber = Math.floor(chainTimestamp / 86400)
  const canAccrueExchangeRate = poolDynamicOverviewCTO && poolDynamicOverviewCTO.exchangeRateSetDay < chainDayNumber

  logOT.debug('BorrowerAdminPoolActionModals canRemovePool', canRemovePool, roleEnum, stageName)

  return (
    <Box
      data-testid={'DynamicBorrowerAdminPoolActionModals'}
      px={0}
      py={2}
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        direction: 'row',
        gap: 2,
      }}
    >
      {isCompoundingVault && (
        <ButtonModal title={'Accrue'} variant="primary" disabled={!canAccrueExchangeRate} modalWidth={800}>
          <DynamicSetExchangeRateForm poolAddr={poolAddr} accrueMode={true} />
        </ButtonModal>
      )}

      {canRemovePool && (
        <ButtonModal title={'Remove Pool'} variant="primary">
          <AdminPoolRemoveForm poolAddr={poolAddr} />
        </ButtonModal>
      )}
      {canApproveTransferDestination && (
        <ButtonModal title={'Approve Cross-Vault Transfer Destination'} variant="tertiary">
          <AdminApproveTransferDestination poolAddr={poolAddr}></AdminApproveTransferDestination>
        </ButtonModal>
      )}

      {roleEnum === RoleEnum.POOL_ADMIN && (
        <ButtonModal title={'Pool State'} modalWidth={900} variant="primary">
          <DynamicPauseResumeForm poolAddr={poolAddr} />
        </ButtonModal>
      )}
      {[RoleEnum.POOL_ADMIN, RoleEnum.BORROWER].includes(roleEnum ?? RoleEnum.TO_BE_DETERMINED) && (
        <ButtonModal title={'Set Exchange Rate'} modalWidth={900} variant="primary">
          <DynamicSetExchangeRateForm poolAddr={poolAddr} />
        </ButtonModal>
      )}
      {[RoleEnum.POOL_ADMIN, RoleEnum.BORROWER].includes(roleEnum ?? RoleEnum.TO_BE_DETERMINED) && (
        <ButtonModal title={'Set Indicative Interest & Collateral Rates'} modalWidth={900} variant="primary">
          <DynamicSetIndicativeInterestAndCollateralRateForm poolAddr={poolAddr} />
        </ButtonModal>
      )}
    </Box>
  )
}
